import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Lock, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function CommentSection({ postId }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/api/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/auth');
      return;
    }

    if (!user.is_member) {
      toast.error('Membership required to comment');
      navigate('/membership');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/posts/${postId}/comments`, {
        content: newComment
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      setNewComment('');
      fetchComments();
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8">
      <h3 className="text-2xl font-black text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>Comments ({comments.length})</h3>

      {/* Comment Form */}
      {user && user.is_member ? (
        <form onSubmit={handleSubmit} className="mb-6">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={3}
            className="border-2 border-black rounded-xl resize-none"
            required
          />
          <Button
            type="submit"
            disabled={loading || !newComment.trim()}
            className="mt-3 bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold"
          >
            {loading ? 'Posting...' : 'Post Comment'}
          </Button>
        </form>
      ) : (
        <div className="mb-6 p-6 bg-pink-50 border-4 border-black rounded-2xl text-center">
          <Lock className="w-12 h-12 mx-auto mb-3 text-pink-500" />
          <p className="font-bold text-slate-900 mb-2">
            {user ? 'Membership Required' : 'Login Required'}
          </p>
          <p className="text-sm text-slate-600 mb-4">
            {user ? 'Get a membership to comment on posts' : 'Login to comment on this post'}
          </p>
          <Button
            onClick={() => navigate(user ? '/membership' : '/auth')}
            className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold"
          >
            {user ? '🌟 Get Membership' : 'Login / Sign Up'}
          </Button>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map(comment => (
            <div key={comment.comment_id} className="bg-white border-2 border-black rounded-xl p-4">
              <div className="flex items-start gap-3">
                {comment.user_picture ? (
                  <img src={comment.user_picture} alt={comment.user_name} className="w-10 h-10 rounded-full border-2 border-black" />
                ) : (
                  <div className="w-10 h-10 bg-indigo-100 rounded-full border-2 border-black flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-500" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-slate-900">{comment.user_name}</span>
                    <span className="text-xs text-slate-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-slate-700">{comment.content}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}